/**
 * WordPress dependencies
 */
import {
	Button,
	DropdownMenu,
	MenuGroup,
	MenuItem,
} from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { difference, every, groupBy, keys, omitBy } from 'lodash';
import {
	store as NAB_ACTIONS,
	createConversionAction as createConversionActionWithDefaults,
} from '@nab/conversion-actions';
import type {
	ConversionAction as CA,
	ConversionActionType,
	ConversionActionTypeName,
	GoalId,
} from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { store as NAB_EDITOR } from '../../../store';

const CATEGORIES: Record< ConversionActionType[ 'category' ], string > = {
	view: _x( 'View', 'text', 'nelio-ab-testing' ),
	interaction: _x( 'Interaction', 'text', 'nelio-ab-testing' ),
	ecommerce: _x( 'E-Commerce', 'text', 'nelio-ab-testing' ),
	other: _x( 'Other', 'text', 'nelio-ab-testing' ),
};

export const ConversionActionTypeList = (): JSX.Element => {
	const goalId = useActiveGoalId();
	const allConversionActionTypes = useConversionActionTypes();
	const { addConversionActionsIntoGoal } = useDispatch( NAB_EDITOR );

	const createConversionAction = ( type: ConversionActionTypeName ) => {
		if ( ! goalId ) {
			return;
		}
		const act = createConversionActionWithDefaults( type );
		if ( act ) {
			void addConversionActionsIntoGoal( goalId, act );
		}
	};

	if ( ! allConversionActionTypes?.length ) {
		return (
			<div className="nab-conversion-action-type-new">
				<Button variant="secondary" disabled>
					{ _x( 'Add Action', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		);
	}

	const conversionActionTypes = omitBy(
		groupBy( allConversionActionTypes, 'category' ),
		( types ) => ! types.length
	) as Partial<
		Record<
			ConversionActionType[ 'category' ],
			ReadonlyArray< ConversionActionType >
		>
	>;

	return (
		<DropdownMenu
			className="nab-conversion-action-type-new"
			icon={ null }
			label={ _x( 'Add Action', 'command', 'nelio-ab-testing' ) }
			toggleProps={ {
				variant: 'secondary',
				children: _x( 'Add Action', 'command', 'nelio-ab-testing' ),
			} }
			popoverProps={ {
				className: 'nab-conversion-action-type-new__list',
				placement: 'top-center',
			} }
			children={ ( { onClose } ) =>
				keys( CATEGORIES )
					.map( ( c ) => c as ConversionActionType[ 'category' ] )
					.filter( ( c ) => !! conversionActionTypes[ c ]?.length )
					.map( ( c ) => (
						<MenuGroup key={ c } label={ CATEGORIES[ c ] }>
							{ conversionActionTypes[ c ]?.map(
								( { name, title, icon: Icon } ) => (
									<MenuItem
										key={ name }
										role="menuitem"
										icon={ <Icon /> }
										{ ...{ iconPosition: 'left' } }
										onClick={ () => {
											createConversionAction( name );
											onClose();
										} }
									>
										{ title }
									</MenuItem>
								)
							) }
						</MenuGroup>
					) )
			}
		/>
	);
};

// =====
// HOOKS
// =====

const useActiveGoalId = () =>
	useSelect( ( select ) => select( NAB_EDITOR ).getActiveGoal()?.id, [] );

const useConversionActionTypes = () => {
	const goalId = useActiveGoalId();
	const conversionActions = useConversionActions( goalId );
	const conversionActionTypes = useSelect(
		( select ) =>
			select( NAB_ACTIONS )
				.getConversionActionTypes()
				.filter(
					( type ) => ! type.isActive || type.isActive( select )
				)
				.filter(
					( type ) =>
						! type.requiresSingleness ||
						conversionActions.every(
							( ca ) => ca.type !== type.name
						)
				),
		[ conversionActions ]
	);

	if ( ! conversionActions.length ) {
		return conversionActionTypes;
	}

	const uniformTypes = conversionActionTypes.filter(
		( type ) => !! type.requiresUniformity
	);

	for ( const type of uniformTypes ) {
		if ( every( conversionActions, ( a ) => a.type === type.name ) ) {
			return [ type ];
		}
	}

	return uniformTypes.length
		? difference( conversionActionTypes, uniformTypes )
		: conversionActionTypes;
};

const useConversionActions = ( goalId?: GoalId ): ReadonlyArray< CA > =>
	useSelect(
		( select ) => {
			if ( ! goalId ) {
				return [];
			}
			return select( NAB_EDITOR ).getConversionActions( goalId ) || [];
		},
		[ goalId ]
	);
